import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import AuthService from '../services/authService';

type ResetStep = 'validating' | 'form' | 'success' | 'expired';

const ResetPasswordPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';

    const [step, setStep] = useState<ResetStep>('validating');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const authService = AuthService.getInstance();

    useEffect(() => {
        if (!token) {
            setStep('expired');
            return;
        }

        const validateToken = async () => {
            const result = await authService.verifyResetToken(token);
            if (result.valid) {
                setEmail(result.email || '');
                setStep('form');
            } else {
                setStep('expired');
            }
        };

        validateToken();
    }, [authService, token]);

    const handleResetPassword = async () => {
        setError('');

        if (password.length < 8) {
            setError(t('resetPassword.errors.passwordTooShort'));
            return;
        }

        if (password !== confirmPassword) {
            setError(t('resetPassword.errors.passwordsDoNotMatch'));
            return;
        }

        setIsSubmitting(true);

        try {
            await authService.resetPassword(token, password);
            setStep('success');
        } catch (error) {
            setError(error instanceof Error ? error.message : t('common.errors.generic'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/20 blur-3xl"></div>
                <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-primary-300/20 blur-3xl"></div>
            </div>

            <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
                    <div className="bg-white py-8 px-4 shadow-hard sm:rounded-2xl sm:px-10 border border-white/50 backdrop-blur-sm">

                        {step === 'validating' && (
                            <div className="text-center">
                                <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <p className="text-gray-600">{t('resetPassword.validating')}</p>
                            </div>
                        )}

                        {step === 'expired' && (
                            <div className="text-center">
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </div>
                                <h2 className="text-lg font-bold text-gray-900 mb-2">
                                    {t('resetPassword.expired.title')}
                                </h2>
                                <p className="text-sm text-gray-600 mb-6">
                                    {t('resetPassword.expired.body')}
                                </p>
                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                                >
                                    {t('resetPassword.expired.backToLogin')}
                                </button>
                            </div>
                        )}

                        {step === 'form' && (
                            <>
                                <div className="text-center">
                                    <h2 className="text-lg font-bold text-gray-900 mb-2">
                                        {t('resetPassword.form.title')}
                                    </h2>
                                    {email && (
                                        <p className="text-sm text-gray-600 mb-4">
                                            <Trans
                                                i18nKey="resetPassword.form.forEmail"
                                                values={{ email }}
                                                components={{ strong: <span className="font-medium text-gray-900" /> }}
                                            />
                                        </p>
                                    )}
                                </div>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center mb-4">
                                        <svg className="h-5 w-5 text-red-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-sm text-red-600">{error}</p>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('resetPassword.form.newPasswordLabel')}
                                        </label>
                                        <input
                                            id="new-password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                                            placeholder={t('resetPassword.form.newPasswordPlaceholder')}
                                            autoFocus
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('resetPassword.form.confirmPasswordLabel')}
                                        </label>
                                        <input
                                            id="confirm-password"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleResetPassword(); }}
                                            className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                                            placeholder={t('resetPassword.form.confirmPasswordPlaceholder')}
                                        />
                                    </div>

                                    <button
                                        onClick={handleResetPassword}
                                        disabled={isSubmitting || password.length < 8 || confirmPassword === ''}
                                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('resetPassword.form.submitting')}
                                            </div>
                                        ) : (
                                            t('resetPassword.form.submit')
                                        )}
                                    </button>
                                </div>
                            </>
                        )}

                        {step === 'success' && (
                            <div className="text-center">
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h2 className="text-lg font-bold text-gray-900 mb-2">
                                    {t('resetPassword.success.title')}
                                </h2>
                                <p className="text-sm text-gray-600 mb-6">
                                    {t('resetPassword.success.body')}
                                </p>
                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                                >
                                    {t('resetPassword.success.login')}
                                </button>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
